import React from 'react';
import { Crown as CrownIcon, Medal as MedalIcon, Timer as TimerIcon } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../ui/card';
import { PlayerScore, QuizAnswer, QuizQuestion } from '../../../../types/types';
import { Alert, AlertDescription, AlertTitle } from '../../../ui/alert';
import { Badge } from '../../../ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../ui/table';

interface ResultsDisplayProps {
  winners: string[];
  playerScores: Record<string, PlayerScore>;
  questions: QuizQuestion[];
  playerAnswers: Record<string, Record<string, QuizAnswer>>;
  currentUsername: string;
}

/**
 * Component to display game results and player rankings
 */
const ResultsDisplay: React.FC<ResultsDisplayProps> = ({
  winners,
  playerScores,
  questions,
  playerAnswers,
  currentUsername,
}) => {
  // Sort players by score (highest first)
  const sortedPlayers = Object.values(playerScores).sort((a, b) => b.score - a.score);

  // Helper functions to avoid nested ternaries
  const getWinnerContent = () => {
    if (winners.length === 0) {
      return <p>No winners in this game</p>;
    }

    if (winners.length === 1) {
      return (
        <div className='flex items-center mt-2'>
          <CrownIcon className='text-yellow-500 mr-2 h-6 w-6' />
          <span className='text-lg font-medium'>
            {winners[0]} {winners[0] === currentUsername ? '(You)' : ''}
          </span>
        </div>
      );
    }

    return (
      <div className='mt-2'>
        <p className='mb-2'>This game ended in a tie between:</p>
        <div className='flex flex-wrap gap-2'>
          {winners.map(winner => (
            <Badge key={winner} className='bg-yellow-400 text-yellow-900'>
              <CrownIcon className='mr-1 h-4 w-4' />
              {winner} {winner === currentUsername ? '(You)' : ''}
            </Badge>
          ))}
        </div>
      </div>
    );
  };

  const getRankBadge = (index: number) => {
    if (index === 0) {
      return (
        <Badge className='bg-yellow-400 text-yellow-900'>
          <MedalIcon className='mr-1 h-4 w-4' />
          1st
        </Badge>
      );
    }
    if (index === 1) {
      return (
        <Badge variant='outline' className='border-gray-400 text-gray-700'>
          <MedalIcon className='mr-1 h-4 w-4' />
          2nd
        </Badge>
      );
    }
    if (index === 2) {
      return (
        <Badge variant='outline' className='border-amber-800 text-amber-800'>
          <MedalIcon className='mr-1 h-4 w-4' />
          3rd
        </Badge>
      );
    }
    return `${index + 1}th`;
  };

  return (
    <div className='space-y-6'>
      <Alert
        className={
          winners.includes(currentUsername) ? 'bg-green-50 border-green-200' : 'bg-blue-50'
        }>
        <AlertTitle className='text-xl font-bold'>
          {winners.length === 1 ? 'Winner' : 'Winners'}
        </AlertTitle>
        <AlertDescription>{getWinnerContent()}</AlertDescription>
      </Alert>

      <Card>
        <CardHeader>
          <CardTitle>Player Rankings</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Rank</TableHead>
                <TableHead>Player</TableHead>
                <TableHead className='text-right'>Score</TableHead>
                <TableHead className='text-right'>Correct</TableHead>
                <TableHead className='text-right'>
                  <div className='flex items-center justify-end'>
                    <TimerIcon className='mr-1 h-4 w-4' /> Avg Time (s)
                  </div>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedPlayers.map((player, index) => (
                <TableRow
                  key={player.playerId}
                  className={player.playerId === currentUsername ? 'bg-blue-50' : undefined}>
                  <TableCell className='font-medium'>{getRankBadge(index)}</TableCell>
                  <TableCell>
                    {player.playerId}
                    {player.playerId === currentUsername && (
                      <span className='ml-1 text-sm text-gray-500'>(You)</span>
                    )}
                  </TableCell>
                  <TableCell className='text-right font-bold'>{player.score}</TableCell>
                  <TableCell className='text-right'>
                    {player.correctAnswers}/{questions.length}
                  </TableCell>
                  <TableCell className='text-right'>
                    {(player.averageResponseTime / 1000).toFixed(1)}s
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Question Summary</CardTitle>
        </CardHeader>
        <CardContent>
          <div className='space-y-4'>
            {questions.map((question, index) => {
              const playerAnswer = playerAnswers[currentUsername]?.[question.id];
              const isCorrect = playerAnswer?.selectedOption === question.correctAnswer;

              return (
                <div key={question.id} className='border rounded-md p-3'>
                  <div className='flex justify-between mb-2'>
                    <div className='font-medium max-w-[75%]'>
                      Question {index + 1}: {question.question}
                    </div>
                    {playerAnswer ? (
                      <Badge variant={isCorrect ? 'default' : 'destructive'}>
                        {isCorrect ? 'Correct' : 'Incorrect'}
                      </Badge>
                    ) : (
                      <Badge variant='outline'>Not Answered</Badge>
                    )}
                  </div>
                  <div className='grid grid-cols-2 gap-2'>
                    {question.options.map((option: string, optIndex: number) => {
                      let className = 'text-sm p-2 rounded border';
                      if (optIndex === question.correctAnswer) {
                        className += ' bg-green-100 border-green-500';
                      } else if (playerAnswer && playerAnswer.selectedOption === optIndex) {
                        className += ' bg-red-100 border-red-500';
                      }
                      return (
                        <div key={optIndex} className={className}>
                          {String.fromCharCode(65 + optIndex)}: {option}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ResultsDisplay;
